<p-fluid>
    <div class="card flex flex-col gap-6 max-w-3xl !mb-0">
        <div class="font-semibold text-xl">Game Server Status</div>

        <div class="flex flex-col gap-3">
            <div class="flex items-center gap-3">
                <span class="font-medium w-32">Status:</span>
                <p-tag [value]="view().label" [severity]="view().severity" />
            </div>
            <div class="text-muted-color text-sm">{{ view().description }}</div>
            <div class="w-fit">
                <p-button
                    label="Sync"
                    icon="pi pi-sync"
                    [severity]="syncFlash() ?? 'secondary'"
                    [styleClass]="syncFlash() ? 'sync-flash' : ''"
                    [loading]="syncing()"
                    (onClick)="sync()"
                />
            </div>
        </div>

        @if (syncError()) {
        <p-message severity="error" [text]="syncError()!" />
        }
    </div>

    @if (showGames()) {
    <div class="card">
        <div class="font-semibold text-xl mb-4">Games</div>

        @if (gamesService.error()) {
        <p-message
            severity="error"
            [text]="gamesService.error()!"
            styleClass="mb-3"
        />
        }

        <p-table
            [value]="filteredGames()"
            [loading]="gamesService.loading()"
            dataKey="Id"
            [rowTrackBy]="trackById"
            [rows]="10"
            [paginator]="true"
            [showGridlines]="true"
            responsiveLayout="scroll"
        >
            <ng-template #caption>
                <div class="flex items-center justify-between gap-3 flex-wrap">
                    <div class="flex items-center gap-2">
                        <p-button
                            label="Add game"
                            icon="pi pi-plus"
                            (onClick)="openAdd()"
                        />
                        <p-button
                            label="Refresh"
                            icon="pi pi-refresh"
                            severity="secondary"
                            outlined
                            [loading]="gamesService.loading()"
                            (onClick)="refreshGames()"
                        />
                        <p-button
                            label="Generate reviews"
                            icon="pi pi-comments"
                            severity="help"
                            outlined
                            [disabled]="gamesService.games().length === 0"
                            pTooltip="Bulk-generate fake reviews for all games"
                            (onClick)="openBulkReviews()"
                        />
                    </div>
                    <p-iconfield class="ml-auto">
                        <p-inputicon styleClass="pi pi-search" />
                        <input
                            pInputText
                            type="text"
                            placeholder="Search games…"
                            [ngModel]="searchTerm()"
                            (ngModelChange)="searchTerm.set($event ?? '')"
                        />
                    </p-iconfield>
                </div>
            </ng-template>
            <ng-template #header>
                <tr>
                    <th style="width: 5.5rem"></th>
                    <th>Title</th>
                    <th>Game kind</th>
                    <th>Mode</th>
                    <th>Featured</th>
                    <th style="width: 7rem">Status</th>
                    <th style="width: 11rem">Actions</th>
                </tr>
            </ng-template>
            <ng-template #body let-game>
                <tr>
                    <td>
                        @if (effectiveIconUrl(game)) {
                        <img
                            [src]="resolveGameContentUrl(effectiveIconUrl(game))"
                            [alt]="effectiveTitle(game)"
                            class="w-16 h-16 rounded object-cover"
                        />
                        } @else {
                        <div
                            class="w-16 h-16 rounded bg-surface-100 dark:bg-surface-800 flex items-center justify-center"
                        >
                            <i class="pi pi-image text-muted-color text-xl"></i>
                        </div>
                        }
                    </td>
                    <td>
                        <div class="flex flex-col">
                            <span class="font-medium"
                                >{{ effectiveTitle(game) }}</span
                            >
                            @if (effectiveDescription(game); as desc) {
                            <span
                                class="text-xs text-muted-color whitespace-pre-line break-words"
                                >{{ desc }}</span
                            >
                            }
                        </div>
                    </td>
                    <td>{{ gameKindName(game) }}</td>
                    <td>
                        {{
                        gameModeLabel(game.Template.Template.GameKind?.GameMode)
                        }}
                    </td>
                    <td>
                        @if (effectiveIsFeatured(game)) {
                        <i
                            class="pi pi-star-fill text-yellow-500"
                            pTooltip="Featured"
                        ></i>
                        } @else {
                        <i class="pi pi-star text-muted-color"></i>
                        }
                    </td>
                    <td>
                        @if (effectiveIsActive(game)) {
                        <p-tag value="Active" severity="success" />
                        } @else {
                        <p-tag value="Inactive" severity="secondary" />
                        }
                    </td>
                    <td>
                        <div class="flex items-center gap-2">
                            <p-button
                                icon="pi pi-sliders-h"
                                severity="info"
                                [rounded]="true"
                                [outlined]="true"
                                pTooltip="Variants"
                                (onClick)="openVariants(game)"
                            />
                            <p-button
                                icon="pi pi-comments"
                                severity="help"
                                [rounded]="true"
                                [outlined]="true"
                                pTooltip="Reviews"
                                (onClick)="openModeration(game)"
                            />
                            <p-button
                                icon="pi pi-pencil"
                                severity="warn"
                                [rounded]="true"
                                [outlined]="true"
                                pTooltip="Edit"
                                (onClick)="openEdit(game)"
                            />
                        </div>
                    </td>
                </tr>
            </ng-template>
            <ng-template #emptymessage>
                <tr>
                    <td colspan="7" class="text-center py-8 text-muted-color">
                        No games configured for this realm.
                    </td>
                </tr>
            </ng-template>
        </p-table>
    </div>
    }
</p-fluid>

<app-add-game-wizard
    [visible]="wizardVisible()"
    (visibleChange)="wizardVisible.set($event)"
    [realmId]="realm().RealmId"
    [categoryType]="realm().CategorySpecific.type"
    [siteTemplateId]="realm().SiteTemplateId"
/>

<app-edit-game-dialog
    [visible]="editDialogVisible()"
    (visibleChange)="editDialogVisible.set($event)"
    [adminGame]="editingGame()"
    [siteTemplateId]="realm().SiteTemplateId"
/>

<app-variants-dialog
    [visible]="variantsDialogVisible()"
    (visibleChange)="variantsDialogVisible.set($event)"
    [adminGame]="variantsGame()"
/>

<app-review-moderation-dialog
    [visible]="moderationDialogVisible()"
    (visibleChange)="moderationDialogVisible.set($event)"
    [adminGame]="moderationGame()"
/>

<app-bulk-generate-reviews-dialog
    [visible]="bulkReviewsDialogVisible()"
    (visibleChange)="bulkReviewsDialogVisible.set($event)"
    [realmId]="realm().RealmId"
    [games]="gamesService.games()"
/>
